// Two level of filter bars
import React, { useState } from 'react';
import {
  FluentProvider,
  Label,
  webLightTheme,
  makeResetStyles,
  makeStyles,
  shorthands,
  useId,
  tokens,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
} from '@fluentui/react-icons';
import {
  CSMLVideoDuration,
} from '../model';
import {
  NewDropDown,
  AssetTypeToggleButtonsFilter,
  SearchBoxFilter,
  FilterToggleButton,
  ImageSizeFilter,
  VideoDurationFilter,
  AssetSourceFilter,
  ImportSourceFilter,
  DateAddedFilter,
  SortByFilter,
  VideoDurationScrollbarFilter,
} from './components';
import {
  useFilterBarState,
} from './filter-bar-state';

const useFilterBarStyles = makeStyles({
  filterBar: {
    display: 'flex',
    columnGap: '8px',
    rowGap: '8px',
    flexWrap: 'wrap',
    '& button': {
      whiteSpace: 'nowrap',
    },
  },
  remaining: {
    flexGrow: 1,
  },
});

const useBaseAdditionalFilterBarStyles = makeResetStyles({
  marginTop: '12px',
  display: 'flex',
  columnGap: '8px',
  rowGap: '8px',
  flexWrap: 'wrap',
  '& button': {
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
  },
  ...shorthands.borderRadius('4px'),
  ...shorthands.border('1px', 'solid', '#F1F1F1'),
  ...shorthands.padding('8px', '16px'),
  boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.15)',
  alignItems: 'center',
  '& button.clearButton': {
    '&:hover': {
      fontWeight: tokens.fontWeightSemibold,
    },
    color: tokens.colorNeutralForeground2BrandHover,
    fontWeight: 'unset',
  },
});

const useAdditionalFilterBarStyles = makeStyles({
  hide: {
    display: 'none',
  },
  remaining: {
    flexGrow: 1,
  },
  filterText: {
    width: '60px',
  },
});

const AdditionalFilterBar = (props) => {
  const {
    i18n,
    isSelected,
    isImageSelected,
    isVideoSelected,
    logger,
    timeZone,
    dateFormat,
    dateAddedChoiceOptions,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    onDateAddedFilterChange,
    showAdditionalFilterBar,
    isAdsStudioAIGCMVPEnabled,
    importSource,
    importSourceOptions,
    onImportSourceFilterChange,
    assetSources,
    assetSourceOptions,
    onAssetSourceFilterChange,
    minWidthFilter,
    minHeightFilter,
    onImageSizeFilterChange,
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  } = props;

  const baseClasses = useBaseAdditionalFilterBarStyles();
  const classes = useAdditionalFilterBarStyles();
  const filterId = useId('filter');
  const [forceClear, setForceClear] = useState(0);

  return (
    <div
      className={showAdditionalFilterBar ? mergeClasses('additional-filter-bar', baseClasses) : mergeClasses('hided-additional-filter-bar', baseClasses, classes.hide)}
      role="group"
      aria-labelledby={filterId}
    >
      <Label className={classes.filterText} id={filterId}>{i18n.getString(_TL_('Filters'))}</Label>
      <div className={isImageSelected ? 'image-size-filter' : classes.hide}>
        <ImageSizeFilter
          i18n={i18n}
          logger={logger}
          defaultMinWidth={minWidthFilter}
          defaultMinHeight={minHeightFilter}
          onChange={onImageSizeFilterChange}
          forceClear={forceClear}
        />
      </div>
      <div className={isVideoSelected ? 'video-duration-filter' : classes.hide}>
        <VideoDurationFilter
          i18n={i18n}
          logger={logger}
          defaultMaxDuration={maxDurationFilter}
          defaultMinDuration={minDurationFilter}
          onChange={onVideoDurationFilterChange}
          forceClear={forceClear}
        />
      </div>
      <DateAddedFilter
        i18n={i18n}
        logger={logger}
        timeZone={timeZone}
        dateFormat={dateFormat}
        defaultTimeFrame={dateAddedFilter}
        defaultCustomDateRange={dateAddedFilterCustomDateRange}
        options={dateAddedChoiceOptions}
        onChange={onDateAddedFilterChange}
        forceClear={forceClear}
      />
      {isAdsStudioAIGCMVPEnabled &&
        <AssetSourceFilter
          i18n={i18n}
          logger={logger}
          defaultSelectedAssetSources={assetSources}
          options={assetSourceOptions}
          onChange={onAssetSourceFilterChange}
          forceClear={forceClear}
        />}
      {!isAdsStudioAIGCMVPEnabled &&
        <ImportSourceFilter
          i18n={i18n}
          logger={logger}
          forceClear={forceClear}
          importSource={importSource}
          options={importSourceOptions}
          onChange={onImportSourceFilterChange}
        />}
      {isSelected &&
      <Button
        className="clearButton"
        appearance="transparent"
        icon={<Dismiss20Regular />}
        onClick={() => {
          setForceClear(forceClear + 1);
        }}
      >
        {i18n.getString(_TL_('Clear all'))}
      </Button>}
      <div className={classes.remaining} />
    </div>
  );
};

export const VideosFilterBar = (props) => {
  const {
    i18n,
    logger,
    defaultSearchText,
    searchboxPlaceHolder,
    userPreference,
    defaultMinVideoDuration,
    defaultMaxVideoDuration,
  } = props;

  const classes = useFilterBarStyles();
  const {
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
    sortBy,
    sortingOptions,
    onSortByChange,
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  } = useFilterBarState({
    logger,
    i18n,
    userPreference,
    defaultSearchText,
    defaultMinVideoDuration,
    defaultMaxVideoDuration,
  });

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={classes.filterBar}>
        <SearchBoxFilter
          i18n={i18n}
          logger={logger}
          onSearchTextChanging={onSearchTextChanging}
          onSearchTextChange={onSearchTextChange}
          onSearchTextBlur={onSearchTextBlur}
          onSearchTextClear={onSearchTextClear}
          defaultSearchText={defaultSearchText}
          searchboxPlaceHolder={searchboxPlaceHolder}
        />
        <VideoDurationScrollbarFilter
          i18n={i18n}
          min={CSMLVideoDuration.min}
          max={CSMLVideoDuration.max}
          minDurationFilter={minDurationFilter}
          maxDurationFilter={maxDurationFilter}
          onChange={onVideoDurationFilterChange}
          unlimited={CSMLVideoDuration.unlimited}
        />
        <div className={classes.remaining} />
        <SortByFilter
          i18n={i18n}
          logger={logger}
          options={sortingOptions}
          onChange={onSortByChange}
          sortBy={sortBy}
        />
      </div>
    </FluentProvider>
  );
};

export const ImagesFilterBar = (props) => {
  const {
    i18n,
    logger,
    defaultSearchText,
    userPreference,
    searchboxPlaceHolder,
  } = props;

  const classes = useFilterBarStyles();
  const {
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
    sortBy,
    sortingOptions,
    onSortByChange,
  } = useFilterBarState({
    logger,
    i18n,
    userPreference,
    defaultSearchText,
  });

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={classes.filterBar}>
        <SearchBoxFilter
          i18n={i18n}
          logger={logger}
          onSearchTextChanging={onSearchTextChanging}
          onSearchTextChange={onSearchTextChange}
          onSearchTextBlur={onSearchTextBlur}
          onSearchTextClear={onSearchTextClear}
          defaultSearchText={defaultSearchText}
          searchboxPlaceHolder={searchboxPlaceHolder}
        />
        <div className={classes.remaining} />
        <SortByFilter
          i18n={i18n}
          logger={logger}
          options={sortingOptions}
          onChange={onSortByChange}
          sortBy={sortBy}
        />
      </div>
    </FluentProvider>
  );
};

export const LibraryFilterBar = (props) => {
  const {
    i18n,
    logger,
    ianaTimeZone,
    dateFormat,
    defaultSearchText,
    searchboxPlaceHolder,
    isAdsStudioAIGCMVPEnabled,
    onUploadLocalFileClick,
    onUploadLocalFolderClick,
    onUploadImageClick,
    onUploadVideoClick,
    onAIGenerateClick,
    onCreateFolderClick,
    userPreference,
  } = props;

  const classes = useFilterBarStyles();
  // Filter state change will trigger useEffect reload
  const {
    assetTypes,
    assetTypesState,
    toggleAssetType,
    toggleFilterBar,
    showAdditionalFilterBar,
    isAdditionalFilterActive,
    isImageSelected,
    isVideoSelected,
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
    sortBy,
    sortingOptions,
    onSortByChange,
    dateAddedChoiceOptions,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    onDateAddedFilterChange,
    importSource,
    importSourceOptions,
    onImportSourceFilterChange,
    assetSources,
    assetSourceOptions,
    onAssetSourceFilterChange,
    minWidthFilter,
    minHeightFilter,
    onImageSizeFilterChange,
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  } = useFilterBarState({
    logger,
    i18n,
    userPreference,
    defaultSearchText,
  });

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={classes.filterBar}>
        <NewDropDown
          onUploadLocalFileClick={onUploadLocalFileClick}
          onUploadLocalFolderClick={onUploadLocalFolderClick}
          onUploadImageClick={onUploadImageClick}
          onUploadVideoClick={onUploadVideoClick}
          onAIGenerateClick={onAIGenerateClick}
          onCreateFolderClick={onCreateFolderClick}
          i18n={i18n}
          logger={logger}
        />
        <AssetTypeToggleButtonsFilter
          i18n={i18n}
          className={classes.assetTypeToggleButtons}
          options={assetTypes}
          optionsState={assetTypesState}
          toggleSelectedOption={toggleAssetType}
          logger={logger}
        />
        <div className={classes.remaining} />
        <SearchBoxFilter
          i18n={i18n}
          logger={logger}
          onSearchTextChanging={onSearchTextChanging}
          onSearchTextChange={onSearchTextChange}
          onSearchTextBlur={onSearchTextBlur}
          onSearchTextClear={onSearchTextClear}
          defaultSearchText={defaultSearchText}
          searchboxPlaceHolder={searchboxPlaceHolder}
        />
        <FilterToggleButton
          i18n={i18n}
          onClick={() => toggleFilterBar()}
          checked={showAdditionalFilterBar}
          logger={logger}
        />
        <SortByFilter
          i18n={i18n}
          logger={logger}
          options={sortingOptions}
          onChange={onSortByChange}
          sortBy={sortBy}
        />
      </div>
      <AdditionalFilterBar
        i18n={i18n}
        showAdditionalFilterBar={showAdditionalFilterBar}
        isSelected={isAdditionalFilterActive}
        optionsState={assetTypesState}
        isImageSelected={isImageSelected}
        isVideoSelected={isVideoSelected}
        timeZone={ianaTimeZone}
        dateFormat={dateFormat}
        dateAddedChoiceOptions={dateAddedChoiceOptions}
        dateAddedFilter={dateAddedFilter}
        dateAddedFilterCustomDateRange={dateAddedFilterCustomDateRange}
        isAdsStudioAIGCMVPEnabled={isAdsStudioAIGCMVPEnabled}
        onDateAddedFilterChange={onDateAddedFilterChange}
        importSource={importSource}
        importSourceOptions={importSourceOptions}
        onImportSourceFilterChange={onImportSourceFilterChange}
        assetSources={assetSources}
        assetSourceOptions={assetSourceOptions}
        onAssetSourceFilterChange={onAssetSourceFilterChange}
        minWidthFilter={minWidthFilter}
        minHeightFilter={minHeightFilter}
        onImageSizeFilterChange={onImageSizeFilterChange}
        minDurationFilter={minDurationFilter}
        maxDurationFilter={maxDurationFilter}
        onVideoDurationFilterChange={onVideoDurationFilterChange}
        logger={logger}
      />
    </FluentProvider>
  );
};

export const SearchResultFilterBar = (props) => {
  const {
    i18n,
    logger,
    defaultSearchText,
    searchboxPlaceHolder,
  } = props;

  const classes = useFilterBarStyles();
  const {
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
  } = useFilterBarState({ logger, i18n, defaultSearchText });

  return (
    <FluentProvider theme={webLightTheme}>
      <div className={mergeClasses('search-filter-bar', classes.filterBar)}>
        <SearchBoxFilter
          i18n={i18n}
          logger={logger}
          onSearchTextChanging={onSearchTextChanging}
          onSearchTextChange={onSearchTextChange}
          onSearchTextBlur={onSearchTextBlur}
          onSearchTextClear={onSearchTextClear}
          defaultSearchText={defaultSearchText}
          searchboxPlaceHolder={searchboxPlaceHolder}
        />
        <div className={classes.remaining} />
      </div>
    </FluentProvider>
  );
};
